"use client"

import IconFilter from "@/app/(site)/_components/Atoms/Icons/IconFilter"
import {
    useFilterPanelContext,
    useProductGridContext,
    useStaticGridContext
} from "@/app/(site)/contexts/product-grid/ProductGridContext"
import { FOCUS_VISIBLE_INSET_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import dynamic from "next/dynamic"
import { useEffect, useRef } from "react"

const FilterPanelContent = dynamic(
    () =>
        import("@/app/(site)/_components/Organisms/ProductGrids/Filter/FilterPanelContent"),
    { ssr: false }
)

export default function DesktopFilterOverlay() {
    const { isFilterPanelOpen, toggleFilterPanel } = useFilterPanelContext()
    const { filterState } = useProductGridContext()
    const { categoryTranslations } = useStaticGridContext()
    const asideRef = useRef<HTMLElement>(null)
    const totalSelectedFilters = filterState.filters.reduce(
        (acc: number, option: { data: string[] }) =>
            acc + (option.data ? option.data.length : 0),
        0
    )

    // Keep the sidebar's max-height aligned with its current viewport top so the
    // filter list never extends below the fold before the sticky position engages.
    useEffect(() => {
        const aside = asideRef.current
        if (!aside) return

        let rafId = 0
        const update = () => {
            const top = aside.getBoundingClientRect().top
            const available = Math.max(0, window.innerHeight - Math.max(top, 0))
            aside.style.maxHeight = `${available}px`
        }
        const onScroll = () => {
            cancelAnimationFrame(rafId)
            rafId = requestAnimationFrame(update)
        }

        update()
        window.addEventListener("scroll", onScroll, { passive: true })
        window.addEventListener("resize", update)
        return () => {
            cancelAnimationFrame(rafId)
            window.removeEventListener("scroll", onScroll)
            window.removeEventListener("resize", update)
        }
    }, [])

    return (
        <>
            <div className="hidden w-0 shrink-0 lg:block">
                <div className="sticky top-[56px] z-[var(--z-sticky-bar-front)] w-[352px]">
                    <button
                        type="button"
                        onClick={toggleFilterPanel}
                        className={clsx(
                            "flex h-[56px] w-full shrink-0 items-center gap-8 border-r border-t border-col-border-neutral bg-white py-16 pl-24",
                            "transition-[border-color] duration-0 ease-in-out",
                            isFilterPanelOpen
                                ? "border-r-col-border-neutral delay-200"
                                : "border-r-transparent",
                            FOCUS_VISIBLE_INSET_CLASS
                        )}
                        aria-label={
                            isFilterPanelOpen
                                ? categoryTranslations.hideFilterSorting
                                : categoryTranslations.showFilterSorting
                        }
                        aria-expanded={isFilterPanelOpen}
                    >
                        <IconFilter />
                        <span className="pt-3 Trim_Mono_POC_Text_14_130_-5_500_uppercase">
                            {isFilterPanelOpen
                                ? categoryTranslations.hideFilterSorting
                                : categoryTranslations.showFilterSorting}
                        </span>
                        <span className="pt-2 Trim_Mono_POC_Text_14_130_-5_500_uppercase">
                            {totalSelectedFilters > 0 && ` (${totalSelectedFilters})`}
                        </span>
                    </button>
                </div>
            </div>
            <aside
                ref={asideRef}
                aria-hidden={!isFilterPanelOpen}
                inert={!isFilterPanelOpen || undefined}
                className={clsx(
                    "sticky top-[56px] z-[var(--z-sticky-bar)] hidden max-h-[calc(100dvh-56px)] shrink-0 overflow-hidden lg:block",
                    "transition-[width] duration-200 ease-in-out",
                    isFilterPanelOpen ? "w-[352px]" : "w-0"
                )}
            >
                <div
                    className={clsx(
                        "absolute right-0 top-0 z-10 h-full w-px bg-col-border-neutral transition-opacity duration-300 ease-in-out",
                        isFilterPanelOpen ? "opacity-100 delay-100" : "opacity-0"
                    )}
                />
                <div className="flex h-full w-[352px] flex-col bg-white pt-[56px]">
                    <FilterPanelContent />
                </div>
            </aside>
        </>
    )
}
